import './lib/env'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import './index.css'
import App from './App.tsx'

import { ErrorBoundary } from './components/ui/ErrorBoundary'
import TagManager from 'react-gtm-module'
import * as Sentry from "@sentry/react";

Sentry.init({
  dsn: "https://7ffd9cbb1b7c04d36cedf12512bc687c@o4511599859924992.ingest.us.sentry.io/4511599866544128",
  dataCollection: {
    // To disable sending user data and HTTP bodies, uncomment the lines below.
    // userInfo: false,
    // httpBodies: []
  },
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration()
  ],
  // Tracing
  tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.1, // Capture 100% in dev, 10% in prod
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/.*\.supabase\.co\/functions\/v1/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // 10% in dev, change to 100% if needed
  replaysOnErrorSampleRate: 1.0, // 100% on error
  // Enable logs to be sent to Sentry
  enableLogs: true,
  // Filtrar scripts injetados por extensões de navegadores (Chrome, Firefox, Safari, Edge)
  denyUrls: [
    /extensions\//i,
    /^chrome-extension:\/\//i,
    /^chrome:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-extension:\/\//i,
    /^safari-web-extension:\/\//i,
    /^edge-extension:\/\//i,
    /^resource:\/\//i,
  ],
  // Ignorar erros comuns de extensões de terceiros e ruídos do navegador
  ignoreErrors: [
    /Cannot define property.*data.*object is not extensible/i,
    /Cannot define property.*object is not extensible/i,
    /object is not extensible/i,
    /ResizeObserver loop completed with undelivered notifications/i,
    /ResizeObserver loop limit exceeded/i,
    /Non-Error promise rejection captured/i,
  ],
  // Filtro defensivo no beforeSend para descartar qualquer stack trace vindo de extensões
  beforeSend(event, hint) {
    const error = hint?.originalException;
    const errorMessage = (error instanceof Error ? error.message : '') || event.message || '';

    if (/object is not extensible/i.test(errorMessage)) {
      return null;
    }

    const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
    const isExtensionError = frames.some((frame) => {
      const filename = frame.filename || '';
      return (
        filename.includes('chrome-extension://') ||
        filename.includes('moz-extension://') ||
        filename.includes('safari-extension://') ||
        filename.includes('safari-web-extension://') ||
        filename.includes('edge-extension://') ||
        filename.includes('extensions/')
      );
    });

    if (isExtensionError) {
      return null;
    }

    return event;
  },
});

const tagManagerArgs = {
    gtmId: import.meta.env.VITE_GTM_ID as string,
}

if (tagManagerArgs.gtmId) {
    TagManager.initialize(tagManagerArgs)
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 5 minutes — avoids redundant background refetches
      staleTime: 5 * 60 * 1000,
    },
  },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
)
